import { AppShell, Heading, SideMenu, type SideMenuItem } from '@atlas/ui-kit'
import { RouterProvider } from 'react-aria-components'
import { Outlet, useHref, useLocation, useNavigate } from 'react-router-dom'
import { useLogout } from '@/auth/use-logout'
import { useAuth } from '@/hooks/useAuth'
import { initials } from '@/lib/labels'
import { StatusBarHost } from '@/notifications/StatusBarHost'

/** The menu as the Full Path screens draw it: Overview, then Work Orders and its two lists. */
export const KIT_MENU: SideMenuItem[] = [
	{ id: 'overview', label: 'Overview', icon: 'calendar-02', href: '/' },
	{
		id: 'work-orders',
		label: 'Work Orders',
		icon: 'chart-histogram',
		href: '/work-orders',
		children: [
			{ id: 'all-work-orders', label: 'All Work Orders', href: '/work-orders' },
			{ id: 'factory-briefs', label: 'Factory Briefs', href: '/work-orders/briefs' },
		],
	},
]

/** The menu item a path belongs to: a brief's own screen still reads as a work order. */
export function activeMenuId(pathname: string): string | undefined {
	if (pathname === '/') return 'overview'
	if (pathname.startsWith('/work-orders/briefs')) return 'factory-briefs'
	if (pathname.startsWith('/work-orders')) return 'all-work-orders'
	return undefined
}

/**
 * The frame of the screens migrated to the UI Kit (ADR-0044): the kit Heading and Side menu
 * around the page, with the activity status bar kept at the foot so notifications still
 * report there. The rest of the app stays in the legacy `Layout` until its screens move.
 */
export function KitLayout() {
	const navigate = useNavigate()
	const { pathname } = useLocation()
	const { user } = useAuth()
	const logout = useLogout()
	const name = user?.displayName || user?.email || ''

	return (
		// The kit's links are react-aria links; this routes them through React Router.
		<RouterProvider navigate={(to) => void navigate(to)} useHref={useHref}>
			<AppShell
				className="h-screen min-h-0 overflow-hidden"
				heading={<Heading user={{ name, initials: initials(name) }} />}
				sideMenu={<SideMenu items={KIT_MENU} activeId={activeMenuId(pathname)} onLogout={logout} />}
			>
				<div className="flex h-full min-h-0 flex-col">
					{/* `relative` keeps the visually hidden inputs of switches and selects inside this
					    scroll area: without it they lengthen the document, and focusing one scrolls the
					    whole frame out of view. */}
					<div className="relative flex-1 overflow-y-auto [scrollbar-gutter:stable] px-6 pt-6">
						<Outlet />
					</div>
					<StatusBarHost />
				</div>
			</AppShell>
		</RouterProvider>
	)
}
